import { useState } from 'react'
import { stripeService, PRICING_TIERS, PricingTier } from '@/services/stripe.service'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Check, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

interface PricingTableProps {
  currentTier?: PricingTier
  onUpgrade?: () => void
}

export function PricingTable({ currentTier = 'trial', onUpgrade }: PricingTableProps) {
  const [loadingTier, setLoadingTier] = useState<string | null>(null)

  const handleSelectPlan = async (tierId: PricingTier) => {
    const tier = PRICING_TIERS[tierId]

    if (!tier.priceId) {
      toast.error('This plan is not available for purchase')
      return
    }

    setLoadingTier(tierId)

    try {
      await stripeService.redirectToCheckout(tier.priceId)
    } catch (error) {
      toast.error('Failed to start checkout')
      setLoadingTier(null)
    }
  }

  const tiers = [
    PRICING_TIERS.trial,
    PRICING_TIERS.pro,
    PRICING_TIERS.enterprise
  ]

  return (
    <div className="grid gap-6 md:grid-cols-3">
      {tiers.map((tier) => {
        const isCurrentTier = tier.id === currentTier
        const isPopular = tier.id === 'pro'

        return (
          <Card
            key={tier.id}
            className={cn(
              'relative flex flex-col',
              isPopular && 'border-primary shadow-lg'
            )}
          >
            {isPopular && (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                Most Popular
              </Badge>
            )}

            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                {tier.name}
                {isCurrentTier && (
                  <Badge variant="secondary">Current</Badge>
                )}
              </CardTitle>
              <CardDescription>{tier.description}</CardDescription>
            </CardHeader>

            <CardContent className="flex-1">
              <div className="mb-6">
                <span className="text-4xl font-bold">
                  ${tier.price}
                </span>
                {tier.price > 0 && (
                  <span className="text-muted-foreground">/month</span>
                )}
              </div>

              <ul className="space-y-2">
                {tier.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>

            <CardFooter>
              {tier.id === 'trial' ? (
                <Button
                  variant="outline"
                  className="w-full"
                  disabled
                >
                  {isCurrentTier ? 'Current Plan' : 'Free Trial'}
                </Button>
              ) : isCurrentTier ? (
                <Button
                  variant="outline"
                  className="w-full"
                  disabled
                >
                  Current Plan
                </Button>
              ) : (
                <Button
                  className={cn(
                    'w-full',
                    isPopular && 'bg-primary hover:bg-primary/90'
                  )}
                  onClick={() => handleSelectPlan(tier.id as PricingTier)}
                  disabled={loadingTier === tier.id}
                >
                  {loadingTier === tier.id ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    `Upgrade to ${tier.name}`
                  )}
                </Button>
              )}
            </CardFooter>
          </Card>
        )
      })}
    </div>
  )
}
